import React, { useState, useEffect } from 'react';
import {
  Input, Card, Tag, Spin, Empty, Row, Col, Typography,
  Badge, Button, Drawer, Divider, Modal, message,
} from 'antd';
import {
  SearchOutlined, ShoppingCartOutlined, PlusOutlined,
  MinusOutlined, DeleteOutlined, CheckOutlined, UserOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { db } from './firebase';
import { collection, getDocs, addDoc, Timestamp } from 'firebase/firestore';

const { Title, Text } = Typography;

export default function BuvetteMenu() {
  const [snacks, setSnacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [imgErrors, setImgErrors] = useState({});

  // Cart
  const [cart, setCart] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Checkout
  const [clientName, setClientName] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [successVisible, setSuccessVisible] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const snap = await getDocs(collection(db, 'snacks'));
        const items = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((s) => s.available !== false)
          .sort((a, b) => a.id.localeCompare(b.id));
        setSnacks(items);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  // ── Cart helpers ──
  const addToCart = (snack) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.snack.id === snack.id);
      if (existing) return prev.map((i) => i.snack.id === snack.id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { snack, qty: 1 }];
    });
  };

  const updateQty = (snackId, delta) => {
    setCart((prev) =>
      prev.map((i) => i.snack.id === snackId ? { ...i, qty: i.qty + delta } : i).filter((i) => i.qty > 0)
    );
  };

  const removeFromCart = (snackId) => setCart((prev) => prev.filter((i) => i.snack.id !== snackId));

  const cartQty = (snackId) => cart.find((i) => i.snack.id === snackId)?.qty || 0;
  const totalArticles = cart.reduce((s, i) => s + i.qty, 0);
  const total = cart.reduce((sum, i) => sum + i.snack.selling_price * i.qty, 0);

  // ── Checkout ──
  const handleCheckout = async () => {
    if (cart.length === 0) return;
    if (!clientName.trim()) {
      message.warning('Veuillez entrer votre nom.');
      return;
    }
    setCheckoutLoading(true);
    try {
      const productLine = cart.map((i) => `${i.qty > 1 ? `${i.qty}x ` : ''}${i.snack.id}`).join(' - ');
      await addDoc(collection(db, 'buvette'), {
        'Date et Heure': Timestamp.now(),
        'Nom Client': clientName.trim(),
        Produit: productLine,
        Total: parseFloat(total.toFixed(3)),
      });
      setLastReceipt({ clientName: clientName.trim(), productLine, total });
      setSuccessVisible(true);
      setCart([]);
      setClientName('');
      setDrawerOpen(false);
    } catch (e) {
      console.error(e);
      message.error('Erreur lors de la commande. Veuillez réessayer.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const filtered = snacks.filter((s) =>
    s.id.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>

      {/* ── Header ── */}
      <div style={{
        background: '#fff',
        borderBottom: '1px solid #f0f0f0',
        padding: '14px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img
            src="/logogo.png"
            alt="WeStudy"
            style={{ height: 38, objectFit: 'contain' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#262626', lineHeight: 1.2 }}>Buvette</div>
            <div style={{ fontSize: '0.7rem', color: '#8c8c8c', letterSpacing: '0.06em' }}>WeStudy Private Library</div>
          </div>
        </div>

        {/* Search + Cart button */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 1, maxWidth: 500, justifyContent: 'flex-end' }}>
          <Input
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ maxWidth: 260, width: '100%' }}
          />
          <Badge count={totalArticles} size="small">
            <Button
              type={totalArticles > 0 ? 'primary' : 'default'}
              icon={<ShoppingCartOutlined />}
              onClick={() => setDrawerOpen(true)}
              style={{ minWidth: 48 }}
            >
              {totalArticles > 0 && <span style={{ marginLeft: 4 }}>{total.toFixed(3)} DT</span>}
            </Button>
          </Badge>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px 16px 80px' }}>
        {!loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <Title level={4} style={{ margin: 0 }}>🥤 Notre Menu</Title>
            <Tag color="blue">{filtered.length} produit{filtered.length !== 1 ? 's' : ''}</Tag>
            <Tag color="green" style={{ marginLeft: 'auto' }}>
              Cliquez sur un produit pour l'ajouter au panier
            </Tag>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <Spin size="large" />
            <p style={{ marginTop: 16, color: '#8c8c8c' }}>Chargement du menu...</p>
          </div>
        ) : filtered.length === 0 ? (
          <Empty description="Aucun produit trouvé" style={{ padding: 60 }} />
        ) : (
          <Row gutter={[14, 14]} className="buvette-grid">
            {filtered.map((snack, i) => {
              const qty = cartQty(snack.id);
              return (
                <Col key={snack.id} xs={12} sm={8} md={6} lg={4} xl={4}>
                  <Badge count={qty} color="#1890ff" offset={[-8, 8]} style={{ width: '100%', display: 'block' }}>
                    <Card
                      styles={{ body: { padding: 0 } }}
                      onClick={() => addToCart(snack)}
                      style={{
                        borderRadius: 10,
                        overflow: 'hidden',
                        width: '100%',
                        border: qty > 0 ? '2px solid #1890ff' : '1px solid #f0f0f0',
                        background: qty > 0 ? '#e6f7ff' : '#fff',
                        boxShadow: qty > 0 ? '0 4px 16px rgba(24,144,255,0.15)' : '0 1px 6px rgba(0,0,0,0.06)',
                        transition: 'all 0.2s',
                        animation: `fadeUp 0.35s ease both`,
                        animationDelay: `${i * 0.03}s`,
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        if (qty === 0) {
                          e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.12)';
                          e.currentTarget.style.transform = 'translateY(-3px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (qty === 0) {
                          e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,0.06)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      {/* Fixed-height image area */}
                      <div style={{
                        width: '100%', height: 150, flexShrink: 0,
                        background: '#fafafa',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        overflow: 'hidden', borderBottom: '1px solid #f5f5f5',
                        position: 'relative',
                      }}>
                        {snack.image && !imgErrors[snack.id] ? (
                          <img
                            src={snack.image}
                            alt={snack.id}
                            loading="lazy"
                            onError={() => setImgErrors((prev) => ({ ...prev, [snack.id]: true }))}
                            style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 10 }}
                          />
                        ) : (
                          <span style={{ fontSize: '2.5rem', opacity: 0.3 }}>🍫</span>
                        )}
                        <div style={{
                          position: 'absolute', bottom: 6, right: 6,
                          background: qty > 0 ? '#1890ff' : 'rgba(0,0,0,0.18)',
                          color: '#fff', borderRadius: '50%',
                          width: 24, height: 24,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 14, fontWeight: 700,
                          transition: 'background 0.2s',
                        }}>+</div>
                      </div>

                      {/* Fixed-height info area — name always same height, price pinned to bottom */}
                      <div style={{
                        padding: '10px 12px 12px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: 72,
                        flexShrink: 0,
                      }}>
                        {/* Name: always 2 lines max, same height */}
                        <div style={{
                          fontSize: '0.82rem',
                          fontWeight: 600,
                          color: '#262626',
                          lineHeight: 1.35,
                          height: 36,
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}>
                          {snack.id}
                        </div>
                        {/* Price always at bottom */}
                        <Tag color="green" style={{ fontSize: '0.8rem', padding: '1px 8px', margin: 0, alignSelf: 'flex-start' }}>
                          {Number(snack.selling_price).toFixed(3)} DT
                        </Tag>
                      </div>
                    </Card>
                  </Badge>
                </Col>
              );
            })}
          </Row>
        )}
      </div>

      {/* ── Floating cart button (mobile) ── */}
      {totalArticles > 0 && (
        <div style={{
          position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          zIndex: 200, display: 'none',
        }} className="mobile-cart-fab">
          <Button
            type="primary" size="large"
            icon={<ShoppingCartOutlined />}
            onClick={() => setDrawerOpen(true)}
            style={{ borderRadius: 50, padding: '0 24px', boxShadow: '0 4px 16px rgba(24,144,255,0.4)' }}
          >
            Voir le panier · {total.toFixed(3)} DT
          </Button>
        </div>
      )}

      {/* ── Cart Drawer ── */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span><ShoppingCartOutlined style={{ marginRight: 8 }} />Mon Panier</span>
            {cart.length > 0 && (
              <Button size="small" icon={<ClearOutlined />} danger onClick={() => setCart([])}>
                Vider
              </Button>
            )}
          </div>
        }
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        placement="right"
        width={360}
        footer={
          cart.length > 0 ? (
            <div>
              {/* Name input */}
              <div style={{ marginBottom: 12 }}>
                <Text type="secondary" style={{ display: 'block', fontSize: '0.72rem', textTransform: 'uppercase', fontWeight: 600, marginBottom: 6 }}>
                  <UserOutlined style={{ marginRight: 4 }} /> Votre nom *
                </Text>
                <Input
                  placeholder="Entrez votre nom..."
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  size="large"
                  prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                />
              </div>

              {/* Total */}
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 14px', background: '#f6ffed',
                border: '1px solid #b7eb8f', borderRadius: 8, marginBottom: 12,
              }}>
                <Text strong style={{ textTransform: 'uppercase', letterSpacing: '0.06em', color: '#555' }}>Total</Text>
                <Text strong style={{ fontSize: '1.4rem', color: '#389e0d' }}>{total.toFixed(3)} DT</Text>
              </div>

              <Button
                type="primary" size="large" block
                icon={<CheckOutlined />}
                loading={checkoutLoading}
                onClick={handleCheckout}
                disabled={!clientName.trim() || cart.length === 0}
                style={{ height: 48, fontWeight: 700, fontSize: '1rem' }}
              >
                Commander
              </Button>
            </div>
          ) : null
        }
      >
        {cart.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Votre panier est vide" style={{ marginTop: 60 }} />
        ) : (
          <div>
            {cart.map(({ snack, qty }) => (
              <div key={snack.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 0', borderBottom: '1px solid #f5f5f5',
              }}>
                {/* Thumbnail */}
                <div style={{
                  width: 48, height: 48, borderRadius: 8, background: '#f5f5f5',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  overflow: 'hidden',
                }}>
                  {snack.image ? (
                    <img src={snack.image} alt={snack.id}
                      style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : <span style={{ fontSize: '1.4rem' }}>🍫</span>}
                </div>

                <Text style={{ flex: 1, fontSize: '0.85rem', fontWeight: 600 }} ellipsis={{ tooltip: snack.id }}>
                  {snack.id}
                </Text>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Button size="small" icon={<MinusOutlined />}
                    onClick={() => updateQty(snack.id, -1)}
                    style={{ width: 26, height: 26, minWidth: 26, padding: 0 }} />
                  <Text style={{ width: 22, textAlign: 'center', fontWeight: 700 }}>{qty}</Text>
                  <Button size="small" icon={<PlusOutlined />}
                    onClick={() => updateQty(snack.id, 1)}
                    style={{ width: 26, height: 26, minWidth: 26, padding: 0 }} />
                </div>

                <Tag color="green" style={{ minWidth: 68, textAlign: 'right', margin: 0, fontSize: '0.78rem' }}>
                  {(snack.selling_price * qty).toFixed(3)} DT
                </Tag>

                <Button type="text" danger icon={<DeleteOutlined />}
                  onClick={() => removeFromCart(snack.id)}
                  style={{ padding: 0, width: 24, height: 24, minWidth: 24 }} />
              </div>
            ))}
          </div>
        )}
      </Drawer>

      {/* ── Success Modal ── */}
      <Modal
        open={successVisible}
        onOk={() => setSuccessVisible(false)}
        onCancel={() => setSuccessVisible(false)}
        okText="Fermer"
        cancelButtonProps={{ style: { display: 'none' } }}
        centered
        title="✅ Commande enregistrée !"
      >
        {lastReceipt && (
          <div style={{ lineHeight: 2.2 }}>
            <Row>
              <Col span={8}><Text type="secondary">Nom :</Text></Col>
              <Col span={16}><Text strong>{lastReceipt.clientName}</Text></Col>
            </Row>
            <Row>
              <Col span={8}><Text type="secondary">Produit(s) :</Text></Col>
              <Col span={16}><Text>{lastReceipt.productLine}</Text></Col>
            </Row>
            <Divider style={{ margin: '8px 0' }} />
            <Row>
              <Col span={8}><Text type="secondary">Total :</Text></Col>
              <Col span={16}>
                <Text strong style={{ fontSize: '1.2rem', color: '#389e0d' }}>
                  {lastReceipt.total.toFixed(3)} DT
                </Text>
              </Col>
            </Row>
            <div style={{ marginTop: 16, padding: 12, background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8 }}>
              <Text style={{ fontSize: '0.85rem', color: '#874d00' }}>
                💡 Votre commande a été enregistrée. Veuillez vous présenter à la caisse pour régler.
              </Text>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Footer ── */}
      <div style={{
        textAlign: 'center', padding: '16px',
        borderTop: '1px solid #f0f0f0', background: '#fff',
        color: '#bfbfbf', fontSize: '0.75rem',
      }}>
        © WeStudy Private Library · Tunis
      </div>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 480px) {
          .mobile-cart-fab { display: flex !important; }
        }
        .buvette-grid .ant-badge { width: 100%; display: block; }
        .buvette-grid .ant-badge > .ant-card { width: 100%; }
      `}</style>
    </div>
  );
}